import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n';
import { usePermissions } from '../../admin/hooks/usePermissions';
import { useAttempts } from '../hooks/useAttempts';
import { AttemptFilters } from '../components/AttemptFilters';
import { AttemptCard } from '../components/AttemptCard';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { Pagination } from '../../../components/ui/Pagination';

export const MyAttemptsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isPlatformAdmin, isTenantAdmin } = usePermissions();
  const {
    attempts,
    total,
    stats,
    filters,
    availableTenants,
    availableLearners,
    isPrivileged,
    isSuper,
    loading,
    error,
    updateFilters,
    refresh,
  } = useAttempts();

  const isSuperOrAdmin = isPlatformAdmin || isSuper;
  const isPrivilegedAdmin = isTenantAdmin || isPrivileged || isSuperOrAdmin;

  return (
    <div className="attempts-page" style={{ display: 'grid', gap: '20px', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {/* Page Header with unified dark hero banner */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Home', path: '/dashboard' },
          { label: isPrivilegedAdmin ? 'Simulation Attempts' : 'My Simulation Attempts' },
        ]}
        icon="📋"
        title={isPrivilegedAdmin ? 'Simulation Attempts & Performance Ledger' : 'My Simulation Attempts & History'}
        badge={isPrivilegedAdmin ? 'Admin Practice Ledger' : 'Practice Ledger'}
        description={
          isPrivilegedAdmin
            ? 'Monitor learner simulations across organizations, inspect attempt scorecards, and audit historical practice outcomes.'
            : 'Track your simulation sessions, resume active scenarios, and review past practice scorecards.'
        }
        actions={
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="admin-btn admin-btn--primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: '38px',
              padding: '0 16px',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('nav.explore_scenarios', undefined, 'Explore Scenarios')}
          </button>
        }
      />

      {/* KPI Stats Summary Cards */}
      <div
        className="attempts-page__cards"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '14px',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <div
          className={`attempts-stat-card ${filters.status === '' ? 'active' : ''}`}
          onClick={() => updateFilters({ status: '' })}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: filters.status === '' ? '2px solid var(--blue)' : '1px solid var(--line)',
            borderRadius: '14px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: filters.status === '' ? '0 4px 14px rgba(13, 79, 189, 0.15)' : '0 1px 3px rgba(7, 29, 69, 0.05)',
            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            minWidth: 0,
          }}
        >
          <div
            className="attempts-stat-card__icon"
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'var(--blue-soft)',
              color: 'var(--blue)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '18px',
              flexShrink: 0,
            }}
          >
            🎯
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('attempts.stats.total_attempts', undefined, 'Total Attempts')}
            </div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '24px', fontWeight: 700, color: 'var(--ink)' }}>
              {stats.total}
            </div>
          </div>
        </div>

        <div
          className={`attempts-stat-card ${filters.status === 'IN_PROGRESS' ? 'active' : ''}`}
          onClick={() => updateFilters({ status: 'IN_PROGRESS' })}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: filters.status === 'IN_PROGRESS' ? '2px solid var(--warn)' : '1px solid var(--line)',
            borderRadius: '14px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: filters.status === 'IN_PROGRESS' ? '0 4px 14px rgba(217, 119, 6, 0.15)' : '0 1px 3px rgba(7, 29, 69, 0.05)',
            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            minWidth: 0,
          }}
        >
          <div
            className="attempts-stat-card__icon"
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'var(--warn-soft)',
              color: 'var(--warn)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '18px',
              flexShrink: 0,
            }}
          >
            ⏳
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('status.in_progress', undefined, 'In Progress')}
            </div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '24px', fontWeight: 700, color: 'var(--warn)' }}>
              {stats.inProgress}
            </div>
          </div>
        </div>

        <div
          className={`attempts-stat-card ${filters.status === 'COMPLETED' ? 'active' : ''}`}
          onClick={() => updateFilters({ status: 'COMPLETED' })}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: filters.status === 'COMPLETED' ? '2px solid var(--ok)' : '1px solid var(--line)',
            borderRadius: '14px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: filters.status === 'COMPLETED' ? '0 4px 14px rgba(22, 163, 74, 0.15)' : '0 1px 3px rgba(7, 29, 69, 0.05)',
            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            minWidth: 0,
          }}
        >
          <div
            className="attempts-stat-card__icon"
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'var(--ok-soft)',
              color: 'var(--ok)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '18px',
              flexShrink: 0,
            }}
          >
            ✅
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('status.completed', undefined, 'Completed')}
            </div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '24px', fontWeight: 700, color: 'var(--ok)' }}>
              {stats.completed}
            </div>
          </div>
        </div>

        <div
          className={`attempts-stat-card ${filters.status === 'EXPIRED' ? 'active' : ''}`}
          onClick={() => updateFilters({ status: 'EXPIRED' })}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: filters.status === 'EXPIRED' ? '2px solid var(--bad)' : '1px solid var(--line)',
            borderRadius: '14px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: filters.status === 'EXPIRED' ? '0 4px 14px rgba(194, 59, 59, 0.15)' : '0 1px 3px rgba(7, 29, 69, 0.05)',
            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            minWidth: 0,
          }}
        >
          <div
            className="attempts-stat-card__icon"
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'var(--bad-soft)',
              color: 'var(--bad)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '18px',
              flexShrink: 0,
            }}
          >
            ⚠️
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('attempts.stats.expired_inactive', undefined, 'Expired / Inactive')}
            </div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '24px', fontWeight: 700, color: 'var(--bad)' }}>
              {(stats.expired || 0) + (stats.abandoned || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar with Admin Multi-Tenant & Learner Scopes */}
      <AttemptFilters
        status={filters.status}
        search={filters.search}
        stats={stats}
        loading={loading}
        isPlatformAdmin={isSuperOrAdmin}
        isTenantAdmin={isPrivilegedAdmin}
        availableTenants={availableTenants}
        availableLearners={availableLearners}
        selectedTenantId={filters.tenantId || 'all'}
        selectedUserId={filters.userId || 'all'}
        onStatusChange={(status) => updateFilters({ status })}
        onSearchChange={(search) => updateFilters({ search })}
        onTenantChange={(tenantId) => updateFilters({ tenantId })}
        onUserChange={(userId) =>
          updateFilters({
            userId,
            scope: userId === 'own' ? 'own' : userId === 'all' ? 'all' : 'learner',
          })
        }
      />

      {/* Content Area */}
      {loading ? (
        <div
          style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: '16px',
            padding: '60px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            minHeight: '380px',
            boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
          }}
        >
          <div
            className="dashboard-spinner"
            style={{ width: '40px', height: '40px', borderWidth: '3px', borderColor: '#2563eb', borderTopColor: 'transparent' }}
          />
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
              {t('attempts.loading_title', undefined, 'Loading Practice Attempts...')}
            </h4>
            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)' }}>
              {t('attempts.loading_desc', undefined, 'Retrieving simulation sessions, attempt outcomes, and historical practice logs.')}
            </p>
          </div>
        </div>
      ) : error ? (
        <div style={{ padding: '20px', background: 'var(--bad-soft)', border: '1px solid #F1C7C7', borderRadius: '12px', textAlign: 'center' }}>
          <p style={{ color: 'var(--bad)', fontSize: '14px', fontWeight: 500, margin: '0 0 12px' }}>{error}</p>
          <button
            type="button"
            onClick={refresh}
            className="btn primary"
            style={{ padding: '6px 14px', fontSize: '13px', borderRadius: '8px', background: 'var(--bad)', color: '#fff', border: 0, cursor: 'pointer' }}
          >
            {t('common.retry', undefined, 'Retry')}
          </button>
        </div>
      ) : attempts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--line)' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>📋</div>
          <h3 style={{ fontFamily: 'var(--display)', fontSize: '18px', fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
            {t('attempts.no_attempts_title', undefined, 'No attempts matching criteria')}
          </h3>
          <p style={{ color: 'var(--muted)', fontSize: '13.5px', maxWidth: '420px', margin: '8px auto 20px' }}>
            {filters.status || filters.search
              ? t('attempts.no_attempts_filter_hint', undefined, 'Try changing or clearing your search query and status filters.')
              : t('attempts.no_attempts_empty_hint', undefined, 'You have not started any simulation attempts yet. Choose a scenario from the catalogue to begin practice.')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn primary"
            style={{ padding: '10px 18px', background: 'var(--blue)', color: '#fff', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
          >
            {t('attempts.start_scenario_cta', undefined, 'Start a Scenario')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px', maxWidth: '100%', minWidth: 0 }}>
          <div
            className="attempts-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: '16px', maxWidth: '100%', minWidth: 0 }}
          >
            {attempts.map((attempt) => (
              <AttemptCard key={attempt.id} attempt={attempt} />
            ))}
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div style={{ background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--line)', overflow: 'hidden' }}>
              <Pagination
                currentPage={filters.page}
                totalItems={total}
                pageSize={filters.limit || 20}
                onPageChange={(page) => updateFilters({ page })}
                onPageSizeChange={(limit) => updateFilters({ limit, page: 1 })}
                showPageSizeSelector
                pageSizeOptions={[10, 20, 50, 100]}
                itemLabel={t('attempts.pagination_label', undefined, 'attempts')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
